import React from "react";
import { useTranslation } from "react-i18next";
import Typography from "@mui/material/Typography";
import { Divider } from "@mui/material";
import { Row, Col } from "react-bootstrap";
import { FloatingLabelInput } from "./shared/FormLayout/InputTemplates/FloatingLabelInput";
import { DeleteButton } from "./shared/FormLayout/IconButtons/DeleteButton";
import { AddButton } from "./shared/FormLayout/IconButtons/AddButton";
import { FloatingLabelTextarea } from "./shared/FormLayout/InputTemplates/FloatingLabelTextArea";
import { ExtraDetailsKeys } from "./workorders/ExtraDetailsKeys";

interface Props {
  data?: KeyValue[];
  editable?: boolean;
  onEdit: (editedKeyValuePairs: KeyValue[], populateAccessNotes?: boolean) => void;
}

export interface KeyValue {
  key: string;
  value: string;
}

export function ExtraDetails(props: Props) {
  const { t } = useTranslation();
  function handleInputChange(event: any, index: number) {
    const name = event.target.name;
    const value = event.target.value;

    if (props.data) {
      let editedDetails = [...props.data];
      editedDetails[index] = { ...editedDetails[index], [name]: value };
      const populateAccessNotes = checkPopulateAccessNotes(index, editedDetails);
      if (!populateAccessNotes) {
        props.onEdit(editedDetails, populateAccessNotes);
        return;
      }

      props.onEdit(editedDetails);
    }
  }

  function handleAddItem() {
    const editedDetails = [...(props.data || []), { key: "", value: "" }];
    props.onEdit(editedDetails);
  }

  function handleRemoveItem(index: number) {
    if (props.data) {
      let editedDetails = [...props.data];
      editedDetails.splice(index, 1);
      const populateAccessNotes = checkPopulateAccessNotes(index, editedDetails);
      if (!populateAccessNotes) {
        props.onEdit(editedDetails, populateAccessNotes);
        return;
      }
      props.onEdit(editedDetails);
    }
  }

  function checkPopulateAccessNotes(index: number, editedDetails: KeyValue[]) {
    const data = props?.data;
    if (!data || !data[index] || data[index].key !== ExtraDetailsKeys.AccessNotes) return true;

    if (
      (editedDetails.length > 0 && editedDetails[index].value.trim() === "") ||
      !editedDetails.some(({ key }) => key === ExtraDetailsKeys.AccessNotes)
    )
      return false;

    return true;
  }

  return (
    <div>
      <React.Fragment>
        {props.data && props.data.length > 0 ? (
          props.data?.map((keyValuePair, index) => {
            return (
              <React.Fragment key={`fragment-${index}`}>
                <Row key={`formRow-${index}`} className="py-3 w-100 ">
                  <Col xs={12} sm={5} key={`keyCell-${index}`} className={"d-flex align-items-center pl-sm-4"}>
                    {props.editable ? (
                      <FloatingLabelInput
                        keyName={`input-key-${index}`}
                        name="key"
                        value={keyValuePair.key}
                        label="Label"
                        onChange={(event: any) => handleInputChange(event, index)}
                        maxLength={30}
                      />
                    ) : (
                      <Typography key={`valueText-${index}`} variant={"subtitle2"}>
                        {keyValuePair.key}
                      </Typography>
                    )}
                  </Col>
                  <Col xs={12} sm={6} key={`valueCell-${keyValuePair.key}`} className={"d-flex align-items-center"}>
                    {props.editable ? (
                      keyValuePair.key === ExtraDetailsKeys.AccessNotes ? (
                        <FloatingLabelTextarea
                          keyName={`input-value-${index}`}
                          name="value"
                          value={keyValuePair.value}
                          label={"Value"}
                          onChange={(event: any) => handleInputChange(event, index)}
                          maxLength={512}
                        />
                      ) : (
                        <FloatingLabelInput
                          keyName={`input-value-${index}`}
                          name="value"
                          value={keyValuePair.value}
                          label="Value"
                          onChange={(event: any) => handleInputChange(event, index)}
                          maxLength={512}
                        />
                      )
                    ) : (
                      <Typography key={`keyText-${index}`} variant={"body2"}>
                        {keyValuePair.value}
                      </Typography>
                    )}
                  </Col>
                  {props.editable && (
                    <Col
                      xs={12}
                      sm={1}
                      key={`actionCell-${keyValuePair.key}`}
                      className={"d-flex justify-content-center align-items-center"}
                    >
                      <DeleteButton key={`deleteButton-${keyValuePair.key}`} onClick={() => handleRemoveItem(index)} />
                    </Col>
                  )}
                </Row>
                <Divider />
              </React.Fragment>
            );
          })
        ) : props.editable ? (
          <div>{t("workorders.extraDetailExample")}</div>
        ) : (
          <div>{t("workorders.extraDetailsEmpty")}</div>
        )}

        {props.editable && (
          <span className="w-100 d-flex justify-content-center">
            <AddButton onClick={handleAddItem} />
          </span>
        )}
      </React.Fragment>
    </div>
  );
}
